import { useState } from "react";
import { Phone, Mail, MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { submitInquiry } from "@/lib/inquiries.functions";

export function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = useServerFn(submitInquiry);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (sending) return;
    setError(null);
    setSending(true);
    const fd = new FormData(e.currentTarget);
    try {
      await send({
        data: {
          name: String(fd.get("name") ?? ""),
          email: String(fd.get("email") ?? ""),
          phone: String(fd.get("phone") ?? ""),
          service: String(fd.get("service") ?? ""),
          zip: String(fd.get("zip") ?? ""),
          message: String(fd.get("message") ?? ""),
          source: "contact_form",
        },
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError(
        "We couldn't send your request. Please try again or call (801) 598-2226.",
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <section id="contact" className="py-20 md:py-28 bg-ink text-cream">
      <div className="container-prose">
        <div className="grid lg:grid-cols-2 gap-14">
          <div>
            <span className="text-xs tracking-[0.25em] uppercase font-semibold text-accent">Get your masterpiece quote</span>
            <h2 className="mt-3 font-display text-3xl md:text-5xl leading-tight text-cream">Tell us about your project.</h2>
            <p className="mt-5 text-cream/75 leading-relaxed">Robert personally responds to every inquiry within 24 hours. No pushy sales calls — just a real conversation about what you're trying to build.</p>
            <div className="mt-10 space-y-5">
              <ContactRow icon={Phone} label="Call directly" value="(801) 598-2226" href="tel:8015982226" />
              <ContactRow icon={Mail} label="Email" value="rkdtile@msn.com" href="mailto:rkdtile@msn.com" />
              <ContactRow icon={MapPin} label="Service area" value="Park City · Salt Lake · Utah · Davis · Summit Counties" />
            </div>
          </div>
          <div className="bg-cream text-foreground p-8 md:p-10 shadow-[var(--shadow-elegant)]">
            {submitted ? (
              <div className="flex flex-col items-center text-center py-12">
                <CheckCircle2 className="h-14 w-14 text-primary" strokeWidth={1.4} />
                <h3 className="mt-6 font-display text-2xl">Thank you.</h3>
                <p className="mt-3 text-muted-foreground max-w-sm">We'll be in touch within 24 hours to discuss your project.</p>
              </div>
            ) : (
              <form className="space-y-5" onSubmit={handleSubmit}>
                <Field label="Full name" name="name" required />
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Email" name="email" type="email" required />
                  <Field label="Phone" name="phone" type="tel" />
                </div>
                <label className="block">
                  <span className="block text-xs tracking-[0.18em] uppercase font-semibold mb-2">Service needed</span>
                  <select name="service" className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-primary">
                    <option>Bathroom / Shower</option><option>Kitchen / Backsplash</option><option>Flooring Installation</option>
                    <option>Fireplace</option><option>Natural Stone / Mosaic</option><option>Commercial Project</option>
                    <option>Repair / Fix Existing Work</option>
                  </select>
                </label>
                <Field label="Project zip code" name="zip" />
                <label className="block">
                  <span className="block text-xs tracking-[0.18em] uppercase font-semibold mb-2">Tell us about your project</span>
                  <textarea name="message" rows={4} className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-primary resize-none" />
                </label>
                {error && (
                  <p role="alert" className="text-sm text-destructive">{error}</p>
                )}
                <button type="submit" disabled={sending} className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-7 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-primary/90 transition-colors disabled:opacity-70">
                  {sending ? (<>Sending <Loader2 className="h-4 w-4 animate-spin" /></>) : (<>Request Quote <Send className="h-4 w-4" /></>)}
                </button>
                <p className="text-xs text-muted-foreground text-center">We respond within 24 hours. Your information stays with us — never shared.</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, label, value, href }: { icon: React.ComponentType<{ className?: string; strokeWidth?: number }>; label: string; value: string; href?: string; }) {
  const content = (
    <div className="flex items-start gap-4">
      <div className="h-11 w-11 flex-shrink-0 rounded-full border border-accent/40 flex items-center justify-center">
        <Icon className="h-4 w-4 text-accent" strokeWidth={1.6} />
      </div>
      <div>
        <div className="text-[10px] tracking-[0.22em] uppercase text-cream/60">{label}</div>
        <div className="mt-1 text-cream font-display text-lg">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block hover:opacity-80 transition-opacity">{content}</a> : content;
}

function Field({ label, name, type = "text", required }: { label: string; name: string; type?: string; required?: boolean; }) {
  return (
    <div suppressHydrationWarning>
      <label htmlFor={name} className="block text-xs tracking-[0.18em] uppercase font-semibold mb-2">
        {label}{required && <span className="text-primary"> *</span>}
      </label>
      <input id={name} name={name} type={type} required={required} maxLength={255} suppressHydrationWarning className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-primary" />
    </div>
  );
}
